
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, AlertCircle } from "lucide-react";
import { BasicInfoSection } from "./brand-kit/BasicInfoSection";
import { DescriptionSection } from "./brand-kit/DescriptionSection";
import { LogoUploadSection } from "./brand-kit/LogoUploadSection";
import { PreviewPanel } from "./brand-kit/PreviewPanel";
import FirecrawlService from "@/utils/FirecrawlService";

interface BrandKitSectionProps {
  onNext: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

interface FormData {
  missionStatement: string;
  companyDescription: string;
  linkedIn: string;
  facebook: string;
  twitter: string;
  instagram: string;
  logo: File | null;
  logoPreview: string | null;
  orgName: string;
  email: string;
  phone: string;
  address: string;
}

export function BrandKitSection({ onNext, isLoading, setIsLoading }: BrandKitSectionProps) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  const [extractionStatus, setExtractionStatus] = useState("");
  const [formData, setFormData] = useState<FormData>({
    missionStatement: "",
    companyDescription: "",
    linkedIn: "",
    facebook: "",
    twitter: "",
    instagram: "",
    logo: null,
    logoPreview: null,
    orgName: "",
    email: "",
    phone: "",
    address: ""
  });

  const handleFormChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const simulateExtraction = async () => {
    if (!url) {
      setError("Please enter your website URL");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const steps = [
        "Analyzing your website...",
        "Grabbing your logos...",
        "Extracting brand colors...",
        "Learning about your mission..."
      ];

      for (const step of steps) {
        setExtractionStatus(step);
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      const brandData = await FirecrawlService.extractBrandData(url);
      console.log('Extracted brand data:', brandData);

      onNext();
    } catch (error) {
      setError("Failed to extract brand information. Please try manual setup.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ 
          ...prev, 
          logo: file,
          logoPreview: reader.result as string 
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen pt-24">
      {/* Left Panel - Form */}
      <div className="p-8 bg-white overflow-y-auto">
        <div className="max-w-xl mx-auto space-y-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome to CauseConnect
            </h1>
            <p className="text-gray-600">
              Let's get your nonprofit set up to make a bigger impact
            </p>
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Set Up Your Brand Kit
            </h2>
            <p className="text-gray-600">
              We'll automatically extract your brand assets and information
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-8">
            <BasicInfoSection
              formData={formData}
              onChange={handleFormChange}
              isLoading={isLoading}
            />

            <LogoUploadSection
              onFileChange={handleLogoUpload}
              isLoading={isLoading}
            />

            <DescriptionSection
              formData={formData}
              onChange={handleFormChange}
              isLoading={isLoading}
            />
          </div>

          {isLoading && extractionStatus && (
            <div className="flex items-center justify-center space-x-3 text-primary">
              <Loader2 className="h-5 w-5 animate-spin" />
              <p>{extractionStatus}</p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-6">
            <Button variant="outline" onClick={onNext}>
              Skip for Now
            </Button>
            <Button
              onClick={simulateExtraction}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Right Panel - Live Preview */}
      <div className="hidden lg:block bg-gray-50 border-l">
        <PreviewPanel formData={formData} />
      </div>
    </div>
  );
}
